import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { T, useLang } from "@/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_KEYS = ["residenza_fiscale", "famiglia", "immobili", "societa", "finanziario", "debiti", "assicurazioni", "internazionale", "obiettivi"];
const empty = Object.fromEntries(FIELD_KEYS.map((k) => [k, ""]));

export default function Questionario() {
  const [params] = useSearchParams();
  const [lang, setLang] = useLang();
  const t = T[lang].q;
  const FIELDS = t.fields;
  const sessionId = params.get("session_id");
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", ...empty });
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setLoadError(true);
      return;
    }
    axios
      .get(`${API}/questionnaire/${sessionId}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.customer_email) setForm((f) => ({ ...f, email: data.customer_email }));
      })
      .catch(() => setLoadError(true));
  }, [sessionId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim()) {
      toast.error(t.err_required);
      return;
    }
    if (!privacy) {
      toast.error(t.err_privacy);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/questionnaire`, { session_id: sessionId, ...form, privacy });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.err_submit);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="bg-[#200910] text-[#faf6ef] min-h-screen flex items-center justify-center px-6">
        <div className="max-w-xl w-full border border-[#4d1a2a] bg-[#33101c] p-10" data-testid="questionnaire-error">
          <h1 className="font-display uppercase text-2xl">{t.err_t}</h1>
          <p className="text-sm text-[#b4939c] mt-3">{t.err_d}</p>
          <Link to="/" data-testid="q-back-home" className="inline-block mt-6 px-6 py-3 border border-[#d3b870] text-[#d3b870] font-mono-lbl uppercase tracking-[0.14em] text-xs hover:bg-[#d3b870] hover:text-[#200910] transition-colors">{t.err_btn}</Link>
        </div>
      </div>
    );
  }

  if (info && info.paid && !info.mandate_signed && !done) {
    const mt = T[lang].mandate;
    return (
      <div className="bg-[#200910] text-[#faf6ef] min-h-screen flex items-center justify-center px-6">
        <div className="max-w-xl w-full border border-[#4d1a2a] bg-[#33101c] p-10" data-testid="questionnaire-locked">
          <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{mt.title}</span>
          <h1 className="font-display uppercase text-3xl mt-3">{mt.title}</h1>
          <p className="text-sm text-[#b4939c] mt-4">{mt.intro}</p>
          <Link to={`/incarico?session_id=${sessionId}`} data-testid="q-goto-mandate"
            className="inline-block mt-8 px-6 py-3 bg-[#d3b870] text-[#200910] font-display uppercase tracking-wider text-sm hover:bg-[#ecd9ab] transition-colors">
            {mt.sign_btn}
          </Link>
        </div>
      </div>
    );
  }

  if (info && (!info.paid || info.already_submitted || done)) {
    const msg = done || info.already_submitted
      ? { t: t.done_t, d: t.done_d }
      : { t: t.notpaid_t, d: t.notpaid_d };
    return (
      <div className="bg-[#200910] text-[#faf6ef] min-h-screen flex items-center justify-center px-6">
        <div className="max-w-xl w-full border border-[#4d1a2a] bg-[#33101c] p-10" data-testid="questionnaire-done">
          <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.done_k}</span>
          <h1 className="font-display uppercase text-3xl mt-3">{msg.t}</h1>
          <p className="text-sm text-[#b4939c] mt-4">{msg.d}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#200910] text-[#faf6ef] min-h-screen font-sans py-16 px-6">
      <div className="max-w-2xl mx-auto" data-testid="questionnaire-page">
        <div className="flex justify-end mb-6"><LangSwitcher lang={lang} setLang={setLang} /></div>
        <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.kicker}{info?.package_name || t.default_pkg}</span>
        <h1 className="font-display uppercase text-[clamp(28px,4vw,44px)] leading-[0.98] mt-3">{t.title}</h1>
        <p className="text-sm text-[#b4939c] mt-4 leading-relaxed">
          {t.intro}
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="questionnaire-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block font-mono-lbl text-xs uppercase tracking-[0.14em] text-[#d3b870] mb-2" htmlFor="q-nome">{t.nome}</label>
              <input id="q-nome" data-testid="q-nome" required value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="w-full bg-[#33101c] border border-[#4d1a2a] px-4 py-3 text-sm text-[#faf6ef] focus:border-[#d3b870] outline-none" />
            </div>
            <div>
              <label className="block font-mono-lbl text-xs uppercase tracking-[0.14em] text-[#d3b870] mb-2" htmlFor="q-email">{t.email}</label>
              <input id="q-email" data-testid="q-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#33101c] border border-[#4d1a2a] px-4 py-3 text-sm text-[#faf6ef] focus:border-[#d3b870] outline-none" />
            </div>
          </div>

          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block font-mono-lbl text-xs uppercase tracking-[0.14em] text-[#d3b870] mb-2" htmlFor={`q-${f.key}`}>{f.label}</label>
              {f.textarea ? (
                <textarea id={`q-${f.key}`} data-testid={`q-${f.key}`} rows={3} placeholder={f.ph} value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="w-full bg-[#33101c] border border-[#4d1a2a] px-4 py-3 text-sm text-[#faf6ef] focus:border-[#d3b870] outline-none" />
              ) : (
                <input id={`q-${f.key}`} data-testid={`q-${f.key}`} placeholder={f.ph} value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="w-full bg-[#33101c] border border-[#4d1a2a] px-4 py-3 text-sm text-[#faf6ef] focus:border-[#d3b870] outline-none" />
              )}
            </div>
          ))}

          <label className="flex items-start gap-3 text-xs text-[#b4939c] cursor-pointer" data-testid="q-privacy-label">
            <input type="checkbox" data-testid="q-privacy" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)}
              className="mt-0.5 accent-[#d3b870]" />
            <span>{t.privacy_pre}<Link to="/privacy" data-testid="q-privacy-link" className="text-[#d3b870] underline hover:text-[#ecd9ab]">{t.privacy_link}</Link>{t.privacy_post}</span>
          </label>

          <button type="submit" data-testid="q-submit" disabled={submitting}
            className="w-full py-4 bg-[#d3b870] hover:bg-[#ecd9ab] text-[#200910] font-display uppercase tracking-wider text-sm transition-colors font-bold disabled:opacity-60">
            {submitting ? t.sending : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
